/*
  Size range availability — colour-aware re-striking
  ==================================================
  The size pill (snippets/twp-product-sizes.liquid) renders server-side showing
  availability across the whole product, because no colour is selected when a
  card first paints. Once a colour IS selected, the sizes are re-struck against
  that colour alone.

  The pill carries the per-colour data as space-separated "colour:size" pairs of
  handleised option values, one pair per purchasable variant, plus the handle of
  the colour option itself:

    data-colour-option="colour"
    data-size-availability="charcoal:single charcoal:queen ecru:queen"

  Triggers, in order of reliability:

    1. `change` on the swatch radio — Horizon's swatches block
       (snippets/variant-swatches.liquid) renders real radio inputs named
       "<Option>-<productId>-swatch". This fires the instant the swatch is
       clicked, so the pill updates without waiting on anything.
    2. `variant:update` — dispatched by the variant picker only AFTER its
       section-render fetch resolves (assets/variant-picker.js:391). Kept as a
       backstop so the pill re-syncs if the picker morphs its markup. Listened
       for in the CAPTURE phase, because ProductCard#handleVariantUpdate calls
       stopPropagation() (assets/product-card.js:218).
    3. `twp:swatch-change` — escape hatch for surfaces whose swatches are not a
       variant picker. The custom `_product-card-swatches` block is visual-only,
       so nothing dispatches this yet; wiring it up is a matter of dispatching
       the event with `detail.colour` set to the chosen option value.

  Cards where no colour is ever selected keep the server-rendered whole-product
  state, which is the correct "no colour chosen" answer.
*/
(function () {
  "use strict";

  var PILL_SELECTOR = ".twp-product-sizes";
  var UNAVAILABLE_CLASS = "twp-product-sizes__size--unavailable";

  // Mirrors Liquid's `handleize` closely enough for option-value matching.
  function handleize(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // "charcoal:single charcoal:queen" → { single: true } for colour "charcoal".
  // Returns null for a colour the pill doesn't know about, so an unexpected
  // input never strikes out the whole range. A known colour with no stock at
  // all correctly returns an empty set — every size struck.
  function availableSizesFor(pill, colourHandle) {
    var colours = (pill.getAttribute("data-colour-values") || "").split(/\s+/);
    if (colours.indexOf(colourHandle) === -1) return null;

    var sizes = Object.create(null);
    var pairs = (pill.getAttribute("data-size-availability") || "").split(/\s+/);

    for (var i = 0; i < pairs.length; i++) {
      var split = pairs[i].indexOf(":");
      if (split < 1) continue;
      if (pairs[i].slice(0, split) !== colourHandle) continue;
      sizes[pairs[i].slice(split + 1)] = true;
    }

    return sizes;
  }

  function applyColour(pill, colourHandle) {
    if (!colourHandle) return;

    var sizes = availableSizesFor(pill, colourHandle);
    if (!sizes) return;

    var soldOutLabel = pill.getAttribute("data-sold-out-label") || "";
    var items = pill.querySelectorAll("[data-size]");

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var isAvailable = !!sizes[item.getAttribute("data-size") || ""];

      item.classList.toggle(UNAVAILABLE_CLASS, !isAvailable);

      // Keep the screen-reader "Sold out" suffix in sync with the visual state.
      var note = item.querySelector(".visually-hidden");
      if (isAvailable) {
        if (note) note.remove();
      } else if (!note && soldOutLabel) {
        note = document.createElement("span");
        note.className = "visually-hidden";
        note.textContent = " " + soldOutLabel;
        item.appendChild(note);
      }
    }
  }

  /*
    Is this radio the colour selector for this pill? Horizon names swatch inputs
    "<Option name>-<product id>-swatch", so the handleised name starts with the
    handleised option name the pill recorded. Matching on that rather than
    sniffing the label text keeps multi-swatch products (e.g. colour AND size
    swatches) from cross-wiring.
  */
  function isColourInputFor(pill, input) {
    var colourOption = pill.getAttribute("data-colour-option");
    if (!colourOption) return false;
    return handleize(input.name || "").indexOf(colourOption + "-") === 0;
  }

  function cardFrom(target) {
    if (!target || typeof target.closest !== "function") return null;
    return target.closest("product-card") || target.closest(".product-card");
  }

  // Re-reads the checked colour in the card and applies it to every pill inside.
  function refresh(card) {
    var pills = card.querySelectorAll(PILL_SELECTOR);
    if (!pills.length) return;

    var checked = card.querySelectorAll("input[type='radio']:checked");

    for (var i = 0; i < pills.length; i++) {
      for (var j = 0; j < checked.length; j++) {
        if (!isColourInputFor(pills[i], checked[j])) continue;
        applyColour(pills[i], handleize(checked[j].value));
        break;
      }
    }
  }

  // 1. Swatch clicked — immediate, no network round trip.
  document.addEventListener(
    "change",
    function (event) {
      var input = event.target;
      if (!input || input.type !== "radio") return;

      var card = cardFrom(input);
      if (card) refresh(card);
    },
    true
  );

  /*
    Replaces the card's pill with the one from a section-render response.

    Combined listing swatches resolve to a different product entirely — the
    picker fetches the sibling's card from `section-rendering-product-card`
    (assets/variant-picker.js:308 uses connectedProductUrl) — so the sibling's
    sizes and stock can't be derived from anything already on this card. The
    response is the only source of truth for them.

    Only the contents and data attributes are swapped, never the element, so the
    surface's own positioning classes survive.
  */
  function syncFromResponse(card, html) {
    if (!html || typeof html.querySelector !== "function") return false;

    var source = html.querySelector(PILL_SELECTOR);
    var pill = card.querySelector(PILL_SELECTOR);
    if (!source || !pill) return false;

    pill.innerHTML = source.innerHTML;

    var attrs = ["data-size-availability", "data-colour-option", "data-colour-values"];
    for (var i = 0; i < attrs.length; i++) {
      pill.setAttribute(attrs[i], source.getAttribute(attrs[i]) || "");
    }

    return true;
  }

  // 2. Authoritative pass once the picker's section-render response lands.
  document.addEventListener(
    "variant:update",
    function (event) {
      var card = cardFrom(event.target);
      if (!card) return;

      var html = event.detail && event.detail.data && event.detail.data.html;

      // The picker morphs its own markup while handling this event — wait for
      // that to settle before reading the checked colour back out.
      requestAnimationFrame(function () {
        syncFromResponse(card, html);
        // Re-apply the selected colour: the response renders the whole-product
        // state, which is right for a combined listing sibling (one colour) but
        // still needs narrowing on a genuinely multi-colour product.
        refresh(card);
      });
    },
    true
  );

  // 3. Escape hatch for non-variant-picker swatches.
  document.addEventListener("twp:swatch-change", function (event) {
    var card = cardFrom(event.target);
    if (!card) return;

    var colour = event.detail && event.detail.colour;
    var pills = card.querySelectorAll(PILL_SELECTOR);

    for (var i = 0; i < pills.length; i++) {
      applyColour(pills[i], handleize(colour));
    }
  });

  /*
    Filtered collection and search results pre-select a swatch server-side
    (`allow_preselection` in variant-swatches.liquid), so the pill has to pick
    that up on load rather than waiting for a click.
  */
  function applyPreselected(root) {
    var cards = (root || document).querySelectorAll("product-card");
    for (var i = 0; i < cards.length; i++) refresh(cards[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      applyPreselected(document);
    });
  } else {
    applyPreselected(document);
  }
})();
